/**
 * Review Source Registry
 * The platforms a customer review can originate from.
 * Used by both Builder (for the source selector and list) and Viewer (to pick
 * which brand logo to render on a review card).
 *
 * To add a source:
 *   1. Add an entry to REVIEW_SOURCES below.
 *   2. Add a matching logo to ReviewSourceLogo in the viewer.
 *   3. Widen the CHECK constraint on public.reviews.source in a migration.
 */

// =============================================================================
// Registry
// =============================================================================

export interface ReviewSourceConfig {
  /** Stored in reviews.source */
  readonly key: string
  /** Human-readable brand name, shown in the Builder and beside the logo */
  readonly label: string
}

export const REVIEW_SOURCES = [
  { key: 'google', label: 'Google' },
  { key: 'tripadvisor', label: 'Tripadvisor' },
] as const satisfies readonly ReviewSourceConfig[]

// =============================================================================
// Types & helpers
// =============================================================================

/** Union of valid source keys, derived from the registry (never hand-maintained) */
export type ReviewSource = (typeof REVIEW_SOURCES)[number]['key']

/** Applied to new reviews and to any row predating the source column */
export const DEFAULT_REVIEW_SOURCE: ReviewSource = 'google'

/** Tuple of keys, for building a zod enum in the Builder form */
export const REVIEW_SOURCE_KEYS = REVIEW_SOURCES.map((s) => s.key) as [
  ReviewSource,
  ...ReviewSource[],
]

/**
 * Look up a source's display label.
 * Falls back to the raw key so an unrecognized value (e.g. a source added to the
 * database before this app was redeployed) still renders something sensible.
 */
export function getReviewSourceLabel(source: string | null | undefined): string {
  if (!source) return getReviewSourceLabel(DEFAULT_REVIEW_SOURCE)
  return REVIEW_SOURCES.find((s) => s.key === source)?.label ?? source
}

/** Whether a raw value is a source this build knows how to render */
export function isKnownReviewSource(source: string | null | undefined): source is ReviewSource {
  return !!source && REVIEW_SOURCES.some((s) => s.key === source)
}
